import React, { useState, useEffect } from 'react';
import { verifyCredentials } from '../utils/userStorage';
import { getCurrentUser } from '../utils/userStorage';
import { getApiBaseUrl } from '../config/apiConfig';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';
import { getRecaptchaToken } from '../services/recaptcha';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormErrors {
  email?: string;
  password?: string;
  general?: string;
}

interface LoginFormProps {
  onLoginSuccess: (userData: any) => void;
  onGoToRegister: () => void;
  onGoToAdminLogin?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, onGoToRegister }) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔐 VERIFICAR SI YA HAY UNA SESIÓN ACTIVA Y LIMPIAR AUTCOMPLETADO
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      window.location.href = '/';
      return;
    }
    
    // 🧹 LIMPIAR CUALQUIER VALOR PREVIO EN LOS CAMPOS
    setFormData({
      email: '',
      password: ''
    });
    
    // 🚫 LIMPIAR AUTCOMPLETADO DEL NAVEGADOR
    setTimeout(() => {
      const emailInput = document.getElementById('email') as HTMLInputElement;
      const passwordInput = document.getElementById('password') as HTMLInputElement;
      
      if (emailInput) {
        emailInput.value = '';
        emailInput.setAttribute('autocomplete', 'new-password');
      }
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.setAttribute('autocomplete', 'new-password');
      }
    }, 100);
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    // Validación de email
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'El formato del email no es válido. Ejemplo: usuario@ejemplo.com';
    } else if (formData.email.length > 254) {
      newErrors.email = 'El email es demasiado largo';
    }

    // Validación de contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    } else if (formData.password.length > 128) {
      newErrors.password = 'La contraseña es demasiado larga';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validación en tiempo real
    if (name === 'email' && value.trim()) {
      if (!validateEmail(value)) {
        setErrors(prev => ({
          ...prev,
          email: 'El formato del email no es válido'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          email: undefined
        }));
      }
    } else if (name === 'password' && value) {
      if (value.length < 6) {
        setErrors(prev => ({
          ...prev,
          password: 'La contraseña debe tener al menos 6 caracteres'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          password: undefined
        }));
      }
    } else {
      // Limpiar errores si el campo está vacío o es válido
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Limpiar error general si hay cambios
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;

    // 🔐 VERIFICAR SI YA HAY UNA SESIÓN ACTIVA
    const currentUser = getCurrentUser();
    if (currentUser) {
      window.location.href = '/';
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      console.log('🔐 Intentando login con:', formData.email);
      
      // 🔒 VERIFICACIÓN CRÍTICA: Verificar que el servidor esté disponible
      try {
        const healthCheck = await fetch(`${getApiBaseUrl()}/health`);
        if (!healthCheck.ok) {
          throw new Error('Servidor no disponible');
        }
        console.log('✅ Servidor disponible, procediendo con login');
      } catch (serverError) {
        console.error('❌ Servidor no disponible:', serverError);
        setErrors({
          general: 'El servidor no está disponible. Verifica tu conexión e inténtalo de nuevo.'
        });
        return;
      }
      
      // Verificar credenciales contra la base de datos
      const recaptchaToken = await getRecaptchaToken('login');
      const user = await verifyCredentials(formData.email, formData.password, recaptchaToken);
      
      // ✅ VERIFICACIÓN ESTRICTA: Solo permitir acceso si el usuario es válido y tiene ID
      if (user && user.id && user.email && user.role) {
        // Login exitoso - credenciales verificadas correctamente
        console.log('✅ Login exitoso para:', user.email);
        console.log('👤 Datos del usuario:', user);
        console.log('🔒 Verificación de seguridad: PASÓ');
        
        // Usar directamente el objeto user que ya tiene todos los campos correctos
        onLoginSuccess(user);
      } else {
        setErrors({
          general: 'Email o contraseña incorrectos.'
        });
      }
      
    } catch (error: any) {
      console.error('❌ Error en el login:', error);
      const message = String(error?.message || '');
      const backendMessage = message.includes('Cuenta bloqueada') || message.includes('Demasiados intentos')
        ? message
        : message.includes('servidor no está disponible')
          ? 'El servidor no está disponible. Verifica tu conexión e inténtalo de nuevo.'
          : 'Email o contraseña incorrectos.';

      setErrors({ general: backendMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#f2f4f7_100%)] px-4 py-10">
      <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_460px]">
        <div className="hidden rounded-[2.5rem] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#111827_48%,#1f2937_100%)] p-10 text-white shadow-[0_34px_90px_-45px_rgba(15,23,42,0.6)] lg:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/55">{PEEKPASS_BRAND}</p>
          <h1 className="mt-6 text-5xl font-black tracking-[-0.06em]">Accedé a la ticketera premium.</h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-white/72">
            Gestioná compras, recibi pagos, hace tus publicaciones y cobros desde una experiencia clara,y profesional.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            <div className="rounded-[1.6rem] border border-white/10 bg-white/5 p-5">
              <p className="text-sm font-semibold">Comprá en segundos</p>
              <p className="mt-2 text-sm text-white/65">Checkout simple, tickets digitales y confirmación por email.</p>
            </div>
            <div className="rounded-[1.6rem] border border-white/10 bg-white/5 p-5">
              <p className="text-sm font-semibold">Publicá con Mercado Pago</p>
              <p className="mt-2 text-sm text-white/65">Vinculá tu cuenta y mantené intacto el flujo actual de cobro.</p>
            </div>
          </div>
        </div>

        <div className="w-full rounded-[2.2rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.2)]">
        <div className="mb-8 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">{PEEKPASS_BRAND}</p>
          <h1 className="mt-3 text-3xl font-bold tracking-[-0.04em] text-gray-950">Iniciar sesión</h1>
          <p className="mt-2 text-gray-500">Ingresá a tu cuenta para comprar entradas o gestionar eventos</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campo oculto para confundir al autocompletado */}
          <input
            type="text"
            name="fake-email"
            autoComplete="off"
            style={{ display: 'none' }}
            tabIndex={-1}
          />
          <input
            type="password"
            name="fake-password"
            autoComplete="off"
            style={{ display: 'none' }}
            tabIndex={-1}
          />
          
          {/* Error general */}
          {errors.general && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">
                {errors.general}
              </p>
            </div>
          )}

          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isSubmitting}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="tu@email.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Contraseña */}
          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isSubmitting}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Tu contraseña"
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* Botón de envío */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full rounded-2xl px-4 py-3.5 font-medium transition-colors ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-black hover:bg-slate-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2'
            } text-white`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Iniciando sesión...
              </div>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>

        {/* Enlace a registro */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            ¿No tienes cuenta?{' '}
            <button 
              type="button"
              className="cursor-pointer border-none bg-transparent font-medium text-gray-900 underline hover:text-gray-700"
              onClick={onGoToRegister}
            >
              Crear cuenta
            </button>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginForm;
